import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { InviteMemberButton } from "@components/common/invite-member-button";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { EnableGroupCreationCheckbox } from "@components/groups/enable-group-creation-checkbox";
import { CompanyRoleAutoComplete } from "@components/role/role-autocomplete/company-role-autocomplete";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { Box, Stack } from "@mui-wrapper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { setMany } from "@store/members/members-slice";
import { useAppDispatch } from "@store/store-helper";
import { delay } from "@utils/time-utils";
import { getAssignableWorkspaceRoles } from "@utils/workspace-role-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { createGroupsPermission } from "@utils/user-utils";
import { useState } from "react";
import { Dialog } from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCompanyMembers } from "@hooks/use-company-members";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { InviteSeatsSection } from "@components/table/members/assign-seats/invite-seats-section";
import { getInvitedMembers, getSeatsError, hasTooFewSeats } from "@components/table/members/assign-seats/invite-seats-utils";
import { useGiveInviteSeats } from "@components/table/members/assign-seats/use-give-invite-seats";
import { useInviteSeats } from "@components/table/members/assign-seats/use-invite-seats";
/** Renders the invite member button and the functionality in member page */
export function InviteMemberToCompany(t0) {
  const $ = _c(84);
  const {
    buttonVariant: t1
  } = t0;
  const buttonVariant = t1 === undefined ? "outlined" : t1;
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    respondToUser
  } = useRespondToInvite();
  const {
    trackEvent
  } = useTrackEvent();
  const companyMembers = useCompanyMembers();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const defaultRole = "MEMBER";
  const [selectedRole, setSelectedRole] = useState(defaultRole);
  const [selectedGroupId, setSelectedGroupId] = useState(undefined);
  const [shouldCreateGroups, setShouldCreateGroups] = useState(false);
  const inviteSeats = useInviteSeats(selectedRole);
  const {
    giveInviteSeats
  } = useGiveInviteSeats();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== companyMembers) {
    t3 = companyMembers.map(_temp);
    $[1] = companyMembers;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== companyMembers || $[4] !== inviteSeats.maxInvitedMembers || $[5] !== t3) {
    t4 = {
      entityName: "company",
      existingMembers: companyMembers,
      options: t2,
      companyMemberIds: t3,
      invalidInputMessage: "Enter a valid email address",
      maxInvitedMembers: inviteSeats.maxInvitedMembers
    };
    $[3] = companyMembers;
    $[4] = inviteSeats.maxInvitedMembers;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete(t4);
  let t5;
  if ($[7] !== inviteSeats) {
    t5 = getSeatsError(inviteSeats);
    $[7] = inviteSeats;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const seatsError = t5;
  const shouldShowGroupList = selectedRole === "COMPANY_MANAGER" || selectedRole === "GROUP_VIEWER" || selectedRole === "PROJECT_MANAGER";
  let t6;
  if ($[9] !== inviteSeats || $[10] !== isMemberInputValid || $[11] !== seatsError || $[12] !== selectedGroupId || $[13] !== shouldShowGroupList || $[14] !== uniqueSelectedValues) {
    t6 = shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid) || shouldShowGroupList && !selectedGroupId || !!seatsError || hasTooFewSeats({
      ...inviteSeats,
      invitesCount: uniqueSelectedValues.length
    });
    $[9] = inviteSeats;
    $[10] = isMemberInputValid;
    $[11] = seatsError;
    $[12] = selectedGroupId;
    $[13] = shouldShowGroupList;
    $[14] = uniqueSelectedValues;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const shouldDisableConfirm = t6;
  let t7;
  if ($[16] !== inviteSeats || $[17] !== reset) {
    t7 = function onOpenInviteDialog() {
      reset();
      setIsDialogOpen(true);
      setSelectedRole("MEMBER");
      setSelectedGroupId(undefined);
      inviteSeats.resetSeats();
    };
    $[16] = inviteSeats;
    $[17] = reset;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const onOpenInviteDialog = t7;
  let createPayloadAndSendInvite;
  if ($[19] !== dispatch || $[20] !== giveInviteSeats || $[21] !== inviteSeats.checkedSeats || $[22] !== respondToUser || $[23] !== selectedGroupId || $[24] !== selectedRole || $[25] !== shouldCreateGroups || $[26] !== uniqueSelectedValues) {
    createPayloadAndSendInvite = async function createPayloadAndSendInvite(t8) {
      const {
        shouldAddMemberToGroup,
        companyId: companyId_0
      } = t8;
      const permission = shouldCreateGroups && selectedRole === "COMPANY_MANAGER" ? createGroupsPermission : undefined;
      if (shouldAddMemberToGroup && selectedGroupId) {
        const payload = {
          assignments: [{
            identities: uniqueSelectedValues,
            role: selectedRole,
            permission
          }]
        };
        const response = await faroCoreApiClient.groups.addMembersToGroup({
          companyId: companyId_0,
          groupId: selectedGroupId,
          payload
        });
        await respond(response);
      } else {
        const payload_0 = {
          assignments: [{
            identities: uniqueSelectedValues,
            role: selectedRole,
            permission
          }]
        };
        const response_0 = await faroCoreApiClient.company.addMembersToCompany({
          companyId: companyId_0,
          payload: payload_0
        });
        await respond(response_0);
      }
    };
    async function respond(response_1) {
      const members = (response_1.data.members ?? []).filter(_temp2);
      dispatch(setMany(members));
      respondToUser(response_1, uniqueSelectedValues.length);
      const invitedMembers = getInvitedMembers({
        members,
        emails: uniqueSelectedValues,
        failedInvitations: response_1.errorData?.details.failedInvitations
      });
      await giveInviteSeats(invitedMembers, inviteSeats.checkedSeats);
    }
    $[19] = dispatch;
    $[20] = giveInviteSeats;
    $[21] = inviteSeats.checkedSeats;
    $[22] = respondToUser;
    $[23] = selectedGroupId;
    $[24] = selectedRole;
    $[25] = shouldCreateGroups;
    $[26] = uniqueSelectedValues;
    $[27] = createPayloadAndSendInvite;
  } else {
    createPayloadAndSendInvite = $[27];
  }
  let t8;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = async function closeDialog() {
      setIsDialogOpen(false);
      await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
      setIsLoading(false);
    };
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const closeDialog = t8;
  let t9;
  if ($[29] !== companyId || $[30] !== createPayloadAndSendInvite || $[31] !== handleErrorWithToast || $[32] !== inviteSeats.checkedSeats || $[33] !== selectedGroupId || $[34] !== selectedRole || $[35] !== trackEvent || $[36] !== uniqueSelectedValues.length) {
    t9 = async function handleConfirm() {
      setIsLoading(true);
      trackEvent({
        name: WorkspaceEvents.inviteMembers,
        props: {
          numberOfMembers: uniqueSelectedValues.length,
          role: selectedRole,
          hasXgSeat: inviteSeats.checkedSeats.includes("xgSeat"),
          hasSceneSeat: inviteSeats.checkedSeats.includes("sceneSeat")
        }
      });
      ;
      try {
        await createPayloadAndSendInvite({
          shouldAddMemberToGroup: !!selectedGroupId,
          companyId
        });
        closeDialog();
      } catch (t10) {
        const error = t10;
        setIsLoading(false);
        handleErrorWithToast({
          id: `inviteMember-${Date.now().toString()}`,
          title: "Cannot invite member(s)",
          error
        });
      }
    };
    $[29] = companyId;
    $[30] = createPayloadAndSendInvite;
    $[31] = handleErrorWithToast;
    $[32] = inviteSeats.checkedSeats;
    $[33] = selectedGroupId;
    $[34] = selectedRole;
    $[35] = trackEvent;
    $[36] = uniqueSelectedValues.length;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  const handleConfirm = t9;
  let t10;
  if ($[38] !== buttonVariant || $[39] !== onOpenInviteDialog) {
    t10 = <InviteMemberButton buttonVariant={buttonVariant} onClick={onOpenInviteDialog} />;
    $[38] = buttonVariant;
    $[39] = onOpenInviteDialog;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  const t11 = canAssignManagers ? "Invite members to workspace" : "Invite viewers to workspace";
  let t12;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    };
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [];
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== handleMemberSelect || $[44] !== helperText || $[45] !== isNewEmailValid || $[46] !== onInputChange || $[47] !== selectedMembers) {
    t14 = <Stack sx={t12}><MembersAutocomplete isRequired={true} hasAutoFocus={true} options={t13} label="Emails" helperText={helperText} selectedOptions={selectedMembers} isValid={isNewEmailValid} onInputChange={onInputChange} handleMemberSelect={handleMemberSelect} placeHolder="Enter email addresses (Separated by comma, semicolon, or space)" /></Stack>;
    $[43] = handleMemberSelect;
    $[44] = helperText;
    $[45] = isNewEmailValid;
    $[46] = onInputChange;
    $[47] = selectedMembers;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    };
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  let t17;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = role => role && setSelectedRole(role);
    t17 = () => setSelectedRole(defaultRole);
    $[50] = t16;
    $[51] = t17;
  } else {
    t16 = $[50];
    t17 = $[51];
  }
  let t18;
  if ($[52] !== canAssignManagers) {
    t18 = getAssignableWorkspaceRoles("invite", canAssignManagers);
    $[52] = canAssignManagers;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== selectedRole || $[55] !== t18) {
    t19 = <Stack sx={t15}><CompanyRoleAutoComplete selectedRole={selectedRole} shouldAllowAllRolesSelection={true} onChange={t16} onResetSelection={t17} roles={t18} isRequired={true} /></Stack>;
    $[54] = selectedRole;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== selectedGroupId || $[58] !== shouldShowGroupList) {
    t20 = shouldShowGroupList && <Stack sx={{
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    }}><LabelWithHelp title="Group" isRequired={true} /><GroupAutoComplete callbackOnGroupSelected={setSelectedGroupId} selectedGroupId={selectedGroupId} placeholder="Please select a group..." /></Stack>;
    $[57] = selectedGroupId;
    $[58] = shouldShowGroupList;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      "&:not(:empty)": {
        mb: SPACE_ELEMENTS_OF_MODAL
      }
    };
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== selectedMembers.length || $[62] !== selectedRole || $[63] !== shouldCreateGroups) {
    t22 = <Box sx={t21}><EnableGroupCreationCheckbox selectedRole={selectedRole} shouldCreateGroups={shouldCreateGroups} membersCount={selectedMembers.length} setShouldCreateGroups={setShouldCreateGroups} /></Box>;
    $[61] = selectedMembers.length;
    $[62] = selectedRole;
    $[63] = shouldCreateGroups;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== inviteSeats || $[66] !== isLoading || $[67] !== seatsError || $[68] !== uniqueSelectedValues.length) {
    t23 = <InviteSeatsSection inviteSeats={inviteSeats} invitesCount={uniqueSelectedValues.length} error={seatsError} isDisabled={isLoading} />;
    $[65] = inviteSeats;
    $[66] = isLoading;
    $[67] = seatsError;
    $[68] = uniqueSelectedValues.length;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== handleConfirm || $[71] !== isDialogOpen || $[72] !== isLoading || $[73] !== shouldDisableConfirm || $[74] !== t11 || $[75] !== t14 || $[76] !== t19 || $[77] !== t20 || $[78] !== t22 || $[79] !== t23) {
    t24 = <Dialog title={t11} confirmText="Send Invite" open={isDialogOpen} onConfirm={handleConfirm} showSpinner={isLoading} isConfirmDisabled={shouldDisableConfirm} onCancel={closeDialog} dataTestId="invite-members-to-workspace-dialog">{t14}{t19}{t20}{t22}{t23}</Dialog>;
    $[70] = handleConfirm;
    $[71] = isDialogOpen;
    $[72] = isLoading;
    $[73] = shouldDisableConfirm;
    $[74] = t11;
    $[75] = t14;
    $[76] = t19;
    $[77] = t20;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] !== t10 || $[82] !== t24) {
    t25 = <>{t10}{t24}</>;
    $[81] = t10;
    $[82] = t24;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  return t25;
}
function _temp2(member_0) {
  return member_0.kind === "user";
}
function _temp(member) {
  return member.identity;
}